import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { ValidationError } from "@common/validationError.ts";
import TagsMultiSelect from "@common/components/tagsMultiSelect/TagsMultiSelect.tsx";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import { EntityTemplateField, getStandardFieldTranslationKey, SystemStandardField } from "../../entityTemplates/EntityTemplate.types.ts";
import { SystemCustomFieldRenderer, SystemStandardFieldRenderer } from "../../entityTemplates/SystemFieldRenderers.tsx";
import SelectableLegalEntityMultiSelect from "../../legalEntity/SelectableLegalEntityMultiSelect.tsx";
import { SystemModel } from "../systemEdit/SystemEdit.types.ts";
import { SystemCreationFieldSelections } from "../systemCreation.ts";
import SystemAssociatedAssetsCreationField from "../creationFields/SystemAssociatedAssetsCreationField.tsx";
import SystemDataSubjectsCreationField from "../creationFields/SystemDataSubjectsCreationField.tsx";
import SystemDataCategoriesCreationField from "../creationFields/SystemDataCategoriesCreationField.tsx";
import SystemSecurityMeasuresCreationField from "../creationFields/SystemSecurityMeasuresCreationField.tsx";
import SystemPoliciesCreationField from "../creationFields/SystemPoliciesCreationField.tsx";

export interface SystemTemplateFieldProps {
    field: EntityTemplateField;
    name: string;
    onNameChange: (name: string) => void;
    nameErrorText?: string;
    system: Partial<SystemModel>;
    onChange: (changeFunc: (system: SystemModel) => void) => Promise<void>;
    validationErrors: Array<ValidationError>;
    selectedTagIds: Array<string>;
    onTagsChange: (tagIds: Array<string>) => void;
    creationFieldSelections: SystemCreationFieldSelections;
    onCreationFieldChange: (field: SystemStandardField, selectedIds: Array<string>) => void;
}

/**
 * One template field, rendered for an asset that has not been created yet. Every branch here replaces a
 * standard renderer that either saves on its own or needs a real system id. The rest go through the
 * standard renderers untouched.
 */
function SystemTemplateField(props: SystemTemplateFieldProps) {
    const { translateString } = useTranslation();
    const standardField = props.field.standardFieldIdentifier as SystemStandardField | undefined;

    const errorTextFor = (field: SystemStandardField) => props.validationErrors.find((v) => v.field === getStandardFieldTranslationKey(field))?.error;
    const selectionFor = (field: SystemStandardField) => ({
        selectedIds: props.creationFieldSelections[field] ?? [],
        onChange: (selectedIds: Array<string>) => props.onCreationFieldChange(field, selectedIds),
        errorText: errorTextFor(field),
    });

    // The standard name field debounces into the system model; the pane keeps the name on its save model
    // so it can be prefilled from a search and validated before anything else
    if (standardField === SystemStandardField.SystemName) {
        return (
            <DotLegalTextField
                autoFocus
                debounce={false}
                size="small"
                label={translateString("system")}
                value={props.name}
                onChange={props.onNameChange}
                errorText={props.nameErrorText}
            />
        );
    }

    if (standardField === SystemStandardField.SystemTags) {
        return (
            <TagsMultiSelect
                selectedTags={props.selectedTagIds}
                onChange={props.onTagsChange}
                entityType={TagRelationType.System}
                isLoading={false}
                errorText={errorTextFor(SystemStandardField.SystemTags)}
            />
        );
    }

    if (standardField === SystemStandardField.SystemSuppliers) {
        const selection = selectionFor(SystemStandardField.SystemSuppliers);
        return (
            <SelectableLegalEntityMultiSelect
                selectedIds={selection.selectedIds}
                onChange={selection.onChange}
                label={translateString("suppliers")}
                errorText={selection.errorText}
            />
        );
    }

    if (standardField === SystemStandardField.SystemAssociatedAssets) {
        return <SystemAssociatedAssetsCreationField {...selectionFor(SystemStandardField.SystemAssociatedAssets)} disabled={false} />;
    }

    if (standardField === SystemStandardField.SystemDataSubjects) {
        return <SystemDataSubjectsCreationField {...selectionFor(SystemStandardField.SystemDataSubjects)} disabled={false} />;
    }

    if (standardField === SystemStandardField.SystemDataCategories) {
        return <SystemDataCategoriesCreationField {...selectionFor(SystemStandardField.SystemDataCategories)} disabled={false} />;
    }

    if (standardField === SystemStandardField.SystemSecurityMeasures) {
        return <SystemSecurityMeasuresCreationField {...selectionFor(SystemStandardField.SystemSecurityMeasures)} disabled={false} />;
    }

    if (standardField === SystemStandardField.SystemPolicies) {
        return <SystemPoliciesCreationField {...selectionFor(SystemStandardField.SystemPolicies)} disabled={false} />;
    }

    if (standardField) {
        return (
            <SystemStandardFieldRenderer
                field={props.field}
                system={props.system as SystemModel}
                systemId=""
                onChange={props.onChange}
                onRefetchSystem={() => {}}
                validationErrors={props.validationErrors}
                isLoading={false}
                isPreview={false}
                disableAccessWarning
            />
        );
    }

    return (
        <SystemCustomFieldRenderer
            field={props.field}
            system={props.system as SystemModel}
            systemId=""
            onChange={props.onChange}
            validationErrors={props.validationErrors}
            isLoading={false}
            isPreview={false}
        />
    );
}

export default SystemTemplateField;
